import { Level, LevelHelper, LogRecord } from './logger.domain';
import { ConsoleHandler } from './handler';
import type { Handler } from './handler';

export class Logger {

  private static readonly ROOT_NAMESPACE: string = 'ROOT';
  private static readonly ROOT_LOGGER: Logger = new Logger(Logger.ROOT_NAMESPACE, Level.ERROR);
  private static loggerConfigurations: Map<string, Logger> = new Map([[Logger.ROOT_NAMESPACE, Logger.ROOT_LOGGER]]);

  private handler: Handler;

  /**
   * @param namespace Logger namespace. Dot separated
   * @param [tags] Tags to add with every log statement for this logger
   */
  constructor(private namespace: string,
              private level: Level = Level.INFO,
              private tags: Array<string> = []) {
    this.handler = new ConsoleHandler();
  }

  /**
   * @param loggerName Logger name with namespace - dot separated string
   * @param [tags] Tags to add with every log statement for this logger
   * @returns
   */
  static getLogger(namespace: string): Logger {
    let logger: Logger = Logger.loggerConfigurations.get(namespace);
    if (logger === undefined) {
      logger = new Logger(namespace);
      Logger.loggerConfigurations.set(namespace, logger);
    }
    return logger;
  }

  static getRootLogger(): Logger {
    return Logger.ROOT_LOGGER;
  }

  setLevel(level: Level): Logger {
    this.level = level;
    return this;
  }

  getLevel(): Level {
    return this.level;
  }

  setHandler(handler: Handler): Logger {
    this.handler = handler;
    return this;
  }

  setTags(...tags: Array<string>): Logger {
    this.tags = tags;
    return this;
  }

  log(level: Level, message: string, objectToLog: unknown = undefined, ...tags: Array<string>): void {
    this._log(level, message, objectToLog, tags);
  }

  /**
   * @param message
   * @param [objectToLog]
   * @param [tags]
   */
  trace(message: string, objectToLog: unknown = undefined, ...tags: Array<string>): void {
    this._log(Level.TRACE, message, objectToLog, tags);
  }

  /**
   * @param message
   * @param [objectToLog]
   * @param [tags]
   */
  debug(message: string, objectToLog: unknown = undefined, ...tags: Array<string>): void {
    this._log(Level.DEBUG, message, objectToLog, tags);
  }

  /**
   * @param message
   * @param [objectToLog]
   * @param [tags]
   */
  info(message: string, objectToLog: unknown = undefined, ...tags: Array<string>): void {
    this._log(Level.INFO, message, objectToLog, tags);
  }

  /**
   * @param message
   * @param [objectToLog]
   * @param [tags]
   */
  warn(message: string, objectToLog: unknown = undefined, ...tags: Array<string>): void {
    this._log(Level.WARN, message, objectToLog, tags);
  }

  /**
   * @param message
   * @param [objectToLog]
   * @param [tags]
   */
  error(message: string, objectToLog: unknown = undefined, ...tags: Array<string>): void {
    this._log(Level.ERROR, message, objectToLog, tags);
  }

  /**
   * @param level
   * @param message
   * @param [objectToLog] will be saved as data property of the log object
   * @param [tags]
   */
  private _log(level: Level, message: string, objectToLog: unknown, tags: Array<string>): void {
    const logger = this.getEffectiveLogger(level);
    if (logger !== undefined) {
      const record = new LogRecord(level, logger.namespace, message, objectToLog, logger.getTags(tags));
      this.handler.publish(record);
    }
  }

  /**
   * @param tags Statement level tags merged with logger tags
   * @private
   */
  private getTags(tags: Array<string>): Array<string> {
    return [...this.tags, ...tags];
  }

  /**
   * @private
   */
  private getEffectiveLogger(level: Level): Logger {
    let currentName = this.namespace;
    do {
      const logger = Logger.loggerConfigurations.get(currentName);
      if (logger !== undefined && this.getLevelPriority(logger.level) <= this.getLevelPriority(level)) {
        return logger;
      }
      const pos = currentName.lastIndexOf('.');
      currentName = pos > 0 ? currentName.substring(0, pos) : '';
    } while (currentName.length > 0);
    const rootLogger = this.getRootLogger();
    return this.getLevelPriority(rootLogger.level) <= this.getLevelPriority(level) ? rootLogger : undefined;
  }

  private getRootLogger(): Logger {
    return Logger.ROOT_LOGGER;
  }

  private getLevelPriority(level: Level): number {
    return LevelHelper.getPriority(level);
  }
}
